import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
// Ajuste o caminho abaixo conforme a localização real do arquivo 'api.ts' no seu projeto
import { POST } from '../../services/api'
import styles from './ProgressTracker.module.css'

interface ProgressTrackerProps {
  processId: string | null
  checkEndpoint: string
  onComplete?: () => void
  onError?: (error: any) => void
  pollingInterval?: number
  maxTimeout?: number
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  processId,
  checkEndpoint,
  onComplete,
  onError,
  pollingInterval = 8000,
  maxTimeout = 30 * 60 * 1000 // 30 minutos
}) => {
  const [isActivePage, setIsActivePage] = useState(true)

  const checkStatus = async (id: string) => {
    try {
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Tempo limite excedido')), 8000)
      )

      const response = await POST(checkEndpoint, { processId: id })
      const res = await Promise.race([response, timeoutPromise])

      return res.data
    } catch (error) {
      onError?.(error)
      return null
    }
  }

  const startStatusPolling = (id: string) => {
    const interval = setInterval(async () => {
      if (!isActivePage) {
        clearInterval(interval)
        return
      }

      const status = await checkStatus(id)
      
      if (status) {
        switch (status.status) {
          case 'completed':
            clearInterval(interval)
            if (isActivePage) {
              Swal.fire({
                icon: 'success',
                title: 'Processo concluído',
                text: status.message || 'O processo foi concluído com sucesso!'
              })
              onComplete?.()
            }
            break

          case 'failed':
            clearInterval(interval)
            if (isActivePage) {
              Swal.fire({
                icon: 'error',
                title: 'Falha no processo',
                text: status.error || 'Ocorreu um erro durante o processo'
              })
            }
            break

          case 'processing':
            if (isActivePage) {
              if (!Swal.isVisible()) {
                Swal.fire({
                  title: 'Processando',
                  html: `
                    <div id="progress-status-message" class="${styles['progress-status-message']} mb-4">
                      ${status.message || 'Processo em andamento...'}
                    </div>
                    <div class="${styles['progress']}" style="height: 24px;">
                      <div 
                        id="progress-bar"
                        class="${styles['progress-bar']} ${styles['progress-bar-striped']} ${styles['progress-bar-animated']} bg-primary" 
                        role="progressbar" 
                        style="width: ${status.progress}%; font-weight: 600; font-size: 14px;"
                      >
                        ${status.progress}%
                      </div>
                    </div>
                    <div id="progress-details" class="${styles['progress-details']} mt-3 text-muted">
                      <small>${status.currentStep || 'Processando...'}</small>
                    </div>
                  `,
                  showConfirmButton: false,
                  allowOutsideClick: false,
                  customClass: {
                    popup: 'progress-status-popup',
                    title: 'progress-status-title'
                  }
                })
              } else {
                const updateElement = (id: string, updater: (element: HTMLElement) => void) => {
                  const element = document.getElementById(id)
                  if (element) updater(element)
                }

                updateElement('progress-status-message', (el) => {
                  el.innerHTML = status.message || 'Processo em andamento...'
                })

                updateElement('progress-bar', (el) => {
                  el.style.width = `${status.progress}%`
                  el.innerHTML = `${status.progress}%`
                })

                updateElement('progress-details', (el) => {
                  el.innerHTML = `<small>${status.currentStep || 'Processando...'}</small>`
                })
              }
            }
            break
        }
      }
    }, pollingInterval)

    const timeout = setTimeout(() => {
      clearInterval(interval)
      if (isActivePage) {
        Swal.fire({
          icon: 'warning',
          title: 'Tempo limite excedido',
          text: 'O processo excedeu o tempo limite.'
        })
      }
    }, maxTimeout)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }

  useEffect(() => {
    setIsActivePage(true)
    return () => setIsActivePage(false)
  }, [])

  useEffect(() => {
    if (processId) {
      const cleanup = startStatusPolling(processId)
      return () => cleanup()
    }
  }, [processId])

  return null // Componente não renderiza nada visualmente
}